// src/features/media/TextTrackUtils.ts

import { TEXT_TRACK_PENDING_MAX_AGE_MS } from '@/lib/constants/media';
import type { ITextTrackData } from '@/lib/types/Media';

export interface PendingTextTrack {
  track: ITextTrackData;
  addedAtMs: number;
}

export class TextTrackUtils {
  /**
   * A pending entry is confirmed once the server list contains its track.
   * Matches by fileId; falls back to (langCode, ttType) for a defensive
   * placeholder created without a server record (fileId 0).
   */
  static isConfirmed(pending: ITextTrackData, serverTracks: ITextTrackData[]): boolean {
    if (pending.fileId > 0) {
      return serverTracks.some(t => t.fileId === pending.fileId);
    }
    return serverTracks.some(t => t.langCode === pending.langCode && t.ttType === pending.ttType);
  }

  /** Server tracks plus pending entries the server has not returned yet. */
  static mergeWithPending(
    serverTracks: ITextTrackData[],
    pendingTracks: ITextTrackData[]
  ): ITextTrackData[] {
    const unconfirmed = pendingTracks.filter(p => !this.isConfirmed(p, serverTracks));
    return unconfirmed.length === 0 ? serverTracks : [...serverTracks, ...unconfirmed];
  }

  /** Poll only while some pending entry is younger than the max age. */
  static shouldPoll(pending: PendingTextTrack[], nowMs: number): boolean {
    return pending.some(p => nowMs - p.addedAtMs < TEXT_TRACK_PENDING_MAX_AGE_MS);
  }
}
